import { apiFetch } from './client';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
  city: string;
  country_code: string;
  language_code: string;
  sport_code: string;
  logo_object_key: string;
  // Office + contact fields are only returned by the create endpoint today;
  // the list endpoint omits them to keep the payload small. Marked optional
  // so the type works for both call sites.
  office_street?: string;
  office_postal_code?: string;
  office_city?: string;
  contact_email?: string;
  contact_phone?: string;
  org_registration_number?: string;
  created_at: string;
}

interface ListOrgsResponse {
  organizations: Organization[];
}

// Callers coerce missing tokens with `?? null` before calling — keeps
// the apiFetch token type strict under exactOptionalPropertyTypes.
export async function listMyOrganizations(token: string | null): Promise<Organization[]> {
  const res = await apiFetch<ListOrgsResponse>('/api/v1/me/organizations', { token });
  return res.organizations;
}

export interface CreateOrgInput {
  name: string;
  slug?: string;
  city: string;
  country_code: string;
  language_code: string;
  sport_code: string;
  // Office fields are optional. Empty strings are fine — the server maps
  // them to NULL in the DB.
  office_street?: string;
  office_postal_code?: string;
  office_city?: string;
  // Geocoded coordinates of the office address (from the Places pick). Sent
  // together or not at all; they place the club on the discovery map.
  office_latitude?: number;
  office_longitude?: number;
  // Contact info shown on the public website. All optional; server stores
  // empty string when unset.
  contact_email?: string;
  contact_phone?: string;
  org_registration_number?: string;
}

// NameCheckMatch is an established club whose name is confusably similar to a
// proposed one. Only public fields — used to warn the creator during onboarding.
export interface NameCheckMatch {
  name: string;
  slug: string;
  city: string;
}

interface NameCheckResponse {
  matches: NameCheckMatch[];
}

// checkOrganizationName asks the server whether a proposed org name is
// confusably similar to an existing established club in the same country. Used
// by onboarding to show a non-blocking "a similar club already exists" warning
// before creating the org. Returns [] when the name is clear.
export async function checkOrganizationName(
  token: string | null,
  name: string,
  countryCode: string,
): Promise<NameCheckMatch[]> {
  const params = new URLSearchParams({ name, country_code: countryCode });
  const res = await apiFetch<NameCheckResponse>(
    `/api/v1/organizations/name-check?${params.toString()}`,
    { token },
  );
  return res.matches ?? [];
}

export async function createOrganization(
  token: string | null,
  input: CreateOrgInput,
): Promise<Organization> {
  return apiFetch<Organization>('/api/v1/organizations', {
    method: 'POST',
    body: input,
    token,
  });
}

// PendingDeletionOrganization is a soft-deleted org the caller owns, still
// inside the 30-day retention window. The Landing page renders these in their
// own "Pending deletion" section with a Restore button and a countdown
// derived from `restore_deadline`.
export interface PendingDeletionOrganization extends Organization {
  deleted_at: string;
  restore_deadline: string;
}

interface ListPendingDeletionResponse {
  organizations: PendingDeletionOrganization[];
}

export async function listMyPendingDeletionOrganizations(
  token: string | null,
): Promise<PendingDeletionOrganization[]> {
  const res = await apiFetch<ListPendingDeletionResponse>(
    '/api/v1/me/organizations/pending-deletion',
    { token },
  );
  return res.organizations;
}

// softDeleteOrganization sends the typed-confirmation name with the request.
// The server re-checks the match and rejects with `name_mismatch` if the SPA
// somehow let through a typo — defense in depth for the most destructive
// action in the product.
export async function softDeleteOrganization(
  token: string | null,
  slug: string,
  confirmName: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/delete`,
    {
      method: 'POST',
      body: { confirm_name: confirmName },
      token,
    },
  );
}

export interface OrgGeneralInfo {
  name: string;
  logo_object_key: string;
  primary_color: string;
  accent_color: string;
  muted_color: string;
  office_street: string;
  office_postal_code: string;
  office_city: string;
  // Coordinates of the saved office address; null when no pin is set.
  office_latitude: number | null;
  office_longitude: number | null;
  contact_email: string;
  contact_phone: string;
  org_registration_number: string;
}

export async function getOrgGeneral(
  token: string | null,
  slug: string,
): Promise<OrgGeneralInfo> {
  return apiFetch<OrgGeneralInfo>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/general`,
    { method: 'GET', token },
  );
}

// UpdateOrganizationGeneralInput is the admin-class edit surface for the
// org's core identity (display name + logo). Pass an empty string to
// logo_object_key to clear it. Fields are optional — omit to leave
// unchanged. The server enforces admin-class (owner OR administrator) at
// the handler boundary.
export interface UpdateOrganizationGeneralInput {
  name?: string;
  logo_object_key?: string;
  // Brand palette. Pass a 7-char hex (#aabbcc) to set, empty string to
  // clear, or omit to leave unchanged. Used by the websites wizard's
  // Branding step + the org Settings page.
  primary_color?: string;
  accent_color?: string;
  muted_color?: string;
  // Office address. Empty string clears the column; omit to leave unchanged.
  office_street?: string;
  office_postal_code?: string;
  office_city?: string;
  // Office coordinates from the address picker. Send together to move the
  // map pin; clearing office_street ("") also nulls them server-side.
  office_latitude?: number;
  office_longitude?: number;
  // Contact info shown on the public website. Empty string clears; omit to
  // leave unchanged.
  contact_email?: string;
  contact_phone?: string;
  org_registration_number?: string;
}

export async function updateOrganizationGeneral(
  token: string | null,
  slug: string,
  input: UpdateOrganizationGeneralInput,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/general`,
    { method: 'PATCH', body: input, token },
  );
}

export async function restoreOrganization(
  token: string | null,
  slug: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/restore`,
    {
      method: 'POST',
      // POST with no body — apiFetch only sets Content-Type when body is
      // defined, and the server reads no fields from the body.
      token,
    },
  );
}
